import { transient, autoinject, bindable } from "aurelia-framework";
import { type ChartDataModel } from "../../../models/components/chart.models";
import { Guid } from "../../../utilities/guid";
import * as Enumerable from "linq";
import * as moment from "moment";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import "amcharts3";
import "amcharts3/amcharts/pie.js";
import "amcharts3/amcharts/xy.js";
import "amcharts3/amcharts/serial.js";
import "amcharts3/amcharts/gauge.js";
import "amcharts3/amcharts/funnel.js";
import "amcharts3/amcharts/plugins/animate/animate.min.js";
import "amcharts3/amcharts/plugins/dataloader/dataloader.min.js";
import "amcharts3/amcharts/plugins/responsive/responsive.min.js";

const amcharts3 = window["AmCharts"];
amcharts3.clickTimeout = 10;
amcharts3.lastClick = 0;
amcharts3.doubleClickDuration = 300;

@autoinject
@transient()
export class Chart { 

    private _guids: any = {};
    private _attachedToDOM: boolean;
    public hasData: boolean;
    private id = Guid.generateGuid();

    @bindable
    public dataEntries: ChartDataModel;

    @bindable
    public canZoom: boolean = true;

    @bindable
    public noDataLabel: string = "No Data Available";

    @bindable
    public showNoDataIfZero: boolean = false;

    @bindable
    public isDefaultLegendVisible: boolean = false;

    @bindable
    public colors: string[];

    @bindable
    public type: string;

    @bindable
    public fontSize: number = 11;

    @bindable
    public exportEnabled: boolean = true;

    @bindable
    public responsive: boolean = false;

    @bindable
    public formatTooltip: string = "[[label]]: [[formatData]]";

    @bindable
    public title: string = "";

    @bindable
    public exportDivId: string;

    @bindable
    public labelFrequency: number = 2;

    @bindable
    public orderBy: string = "descending";

    @bindable
    public dateFormat: string;

    @bindable
    public axes: any[] = [];

    @bindable
    public showHoveringLabels: boolean = true;

    constructor(private element: Element, private configService: ConfigService) {
        const colors = ["#9ee6d2", "#65baf9", "#6dc3c3", "#3379ae", "#e3b7f4", "#ecacac", "#df6262", "#93bf63",
            "#f0be45", "#9ca6e2", "#9fb3b7", "#5e656c"];
        // this.colors = colors;
        this.colors = this.shuffle(colors);
        this.dateFormat = this.configService.config.common.defaultDateFormat;
    }

    public attached(): void {
        this._attachedToDOM = true;
        this.refresh();
    }

    dataEntriesChanged() {
        this.refresh();
    }

    typeChanged() {
        this.refresh();
    }

    private getHasData(): boolean {
        let hasData: boolean =
            this.dataEntries && this.type
            && this.dataEntries.chartDataEntries
            && this.dataEntries.chartDataEntries.length > 0;

        //added for stacked charts where there are results from multiple queries to check if the there is any data in chart data entries 
        hasData = hasData && Enumerable.from(this.dataEntries.chartDataEntries)
            .any(x =>
                x.data != null &&
                ((Array.isArray(x.data) && x.data.length > 0) || !Array.isArray(x.data))
            );

        return hasData;
    }

    public refresh(onlyDataRefresh: boolean = false): void {
        this.hasData = this.getHasData();

        if (this.hasData && this._attachedToDOM) {

            if (onlyDataRefresh && this.chart) {
                this.chart.dataProvider = this.getDataProvider();
                this.chart.validateData();
                return;
            }

            const barCharts = [ChartTypes.Bar, ChartTypes.HorizontalBar, ChartTypes.StackedBar, ChartTypes.StackedHorizontalBar];
            const lineCharts = [ChartTypes.AreaChart, ChartTypes.Line];
            const pie = [ChartTypes.Pie, ChartTypes.Donut];
            const gauge = [ChartTypes.SolidGauge];
            const funnel = [ChartTypes.Funnel];
            const xyChart = [ChartTypes.XYChart];
            const serial = barCharts.concat(lineCharts);
            const baseType = serial.indexOf(this.type) != -1 ? ChartBaseTypes.Serial :
                pie.indexOf(this.type) != -1 ? ChartBaseTypes.Pie :
                    gauge.indexOf(this.type) != -1 ? ChartBaseTypes.Gauge :
                        funnel.indexOf(this.type) != -1 ? ChartBaseTypes.Funnel :
                            (xyChart.indexOf(this.type) != -1 ? ChartBaseTypes.XY : "");

            if (!baseType)
                return;

            if (baseType == ChartBaseTypes.Funnel) {
                // if its a funnel add the total of the dataset
                // let data = Enumerable.from(this.dataEntries.chartDataEntries).sum(x => Number(x.data));
                // this.dataEntries.chartDataEntries.splice(0, 0, {
                //     label: "All",
                //     data: data,
                //     formatData: Number(data.toFixed(2)).toString()
                // })
            }

            let tempColors = this.colors.slice(0);
            // set color
            for (const x of this.dataEntries.chartDataEntries) {
                if (!x.color) {
                    if (tempColors.length <= 0) tempColors = this.shuffle(this.colors.slice(0));
                    const colorIndex = Math.floor(Math.random() * tempColors.length);
                    x.color = tempColors[colorIndex];
                    tempColors.splice(colorIndex, 1);
                }
            }

            const options: any = {
                hideCredits: true,
                path: "views/components/chart/amcharts",
                type: baseType,
                colors: this.dataEntries.chartDataEntries.map(x => x.color),
                fontSize: this.fontSize,
                startEffect: 'easeOutSine',
                startDuration: 0.5,
                sequencedAnimation: true,
                responsive: {
                    enabled: this.responsive
                },
                balloon: {
                    fillAlpha: 1
                },
                export: {
                    enabled: this.exportEnabled,
                    divId: this.exportDivId,
                }
            };

            options.chartCursor = {
                categoryBalloonEnabled: false,
                cursorAlpha: 0.2,
                zoomable: this.canZoom
            }

            //legend
            if (this.isDefaultLegendVisible) {
                options.legend = {
                    position: "right"
                };
            }

            // Chart Options
            this.baseChartConfiguration(baseType, options, lineCharts, barCharts);
            this.concreteChartConfiguration(this.type, options);
            if (this.chart) {
                if (this.chart.AmExport && this.chart.AmExport.setup.menu.parentNode) {
                    this.chart.AmExport.setup.menu.parentNode.removeChild(this.chart.AmExport.setup.menu);
                    this.chart.AmExport.clear();
                }
                this.chart.clear();
            }

            if(window["onChartOptionsBuilt"]){
                window["onChartOptionsBuilt"](options);
            }
            
            const paramsEvent: any = { detail: { options }, bubbles: true };
            const customEv: CustomEvent = new CustomEvent("options-built", paramsEvent);
            this.element.dispatchEvent(customEv);

            this.chart = amcharts3.makeChart(`chartdiv-${this.id}`, options);

            if (baseType == ChartBaseTypes.Serial) {
                for (const graph of options.graphs) {
                    if (!graph || !graph.addListener) continue;
                    graph.addListener("clickGraphItem", (event) => {
                        const paramsEvent: any = { detail: { label: event.item.category, index: event.graph.index, target: event.target }, bubbles: true };

                        const ts = (new Date()).getTime();
                        if ((ts - amcharts3.lastClick) < amcharts3.doubleClickDuration) {
                            // it's double click!
                            // let's clear the timeout so the "click" event does not fire
                            if (amcharts3.clickTimeout) {
                                clearTimeout(amcharts3.clickTimeout);
                            }

                            // reset last click
                            amcharts3.lastClick = 0;

                            // now let's do whatever we want to do on double-click
                            const chartItemClick: CustomEvent = new CustomEvent("chart-item-double-click", paramsEvent);
                            this.element.dispatchEvent(chartItemClick);
                        }
                        else {
                            // single click!
                            // let's delay it to see if a second click will come through
                            amcharts3.clickTimeout = setTimeout(() => {
                                // let's do whatever we want to do on single click
                                const chartItemClick: CustomEvent = new CustomEvent("chart-item-click", paramsEvent);
                                this.element.dispatchEvent(chartItemClick);
                            }, amcharts3.doubleClickDuration);
                        }
                        amcharts3.lastClick = ts;
                    });
                }
            } else if (baseType == ChartBaseTypes.Pie || baseType == ChartBaseTypes.Funnel) {
                //this.chart.chartData[0].dataContext.label
                this.chart.addListener("clickSlice", (event) => {
                    const paramsEvent: any = { detail: { label: event.dataItem.dataContext.label, index: 0, all: (baseType == ChartBaseTypes.Funnel && event.dataItem.dataContext.label.toLowerCase() == "all") }, bubbles: true };
                    const chartItemClick: CustomEvent = new CustomEvent("chart-item-click", paramsEvent);
                    this.element.dispatchEvent(chartItemClick);
                });
            }
        }
    }

    chart: any;

    concreteChartConfiguration(type: string, options: any) {
        if (type == ChartTypes.HorizontalBar || type == ChartTypes.StackedHorizontalBar)
            options.rotate = true;
        else if (type == ChartTypes.AreaChart) {
            options.categoryAxis.labelFrequency = this.labelFrequency;
            options.chartScrollbar = {
                scrollbarHeight: 40,
                backgroundAlpha: 0,
                selectedBackgroundAlpha: 0.1,
                selectedBackgroundColor: "#888888",
                graphFillAlpha: 0,
                graphLineAlpha: 0.5,
                selectedGraphFillAlpha: 0,
                selectedGraphLineAlpha: 1,
                autoGridCount: false,
                gridCount: 5,
                color: "#AAAAAA",
            };
        } else if (type == ChartTypes.Line) {
            options.categoryAxis.labelFrequency = this.labelFrequency;

            options.graphs.forEach(graph => {
                graph.hideBulletsCount = 100;
            });
        }

        if (type == ChartTypes.StackedHorizontalBar || type == ChartTypes.StackedBar) {
            // options.valueAxes = [{
            //     stackType: "regular"
            // }];
        }

    }

    baseChartConfiguration(baseType: string, options: any, lineCharts: string[], barCharts: string[]) {
        if (baseType == ChartBaseTypes.Serial) {
            //Serial
            options.categoryField = "label";
            options.categoryAxis = {
                gridPosition: "start",
                labelRotation: 45,
                labelFunction: (valueText: string) => {
                    if (valueText && valueText.trim().length > 11)
                        return valueText.substring(0, 10) + '...';
                    else
                        return valueText;
                }
            };
            options.graphs = this.dataEntries.labels ?
                this.dataEntries.chartDataEntries.map(x => {
                    const label = this.getGuidFromLabel(x.label);
                    let baloonText = `${x.label ? x.label + ' : ' : ''}[[${label}-formatData]]`;
                    if (this.type == ChartTypes.StackedBar || this.type == ChartTypes.StackedHorizontalBar) {
                        baloonText = `${x.label ? x.label + ' : ' : ''}[[${label}-formatDataStacked]]`;
                    }
                    return {
                        fillColorsField: `${label}-color`,
                        fillAlphas: this.type == ChartTypes.AreaChart ? 0.4 : lineCharts.indexOf(this.type) != -1 ? 0 : 1,
                        lineAlpha: barCharts.indexOf(this.type) != -1 ? 0 : 1,
                        balloonText: baloonText,
                        title: x.label,
                        valueField: `${label}-data`,
                        labelText: `[[${label}-data]]`,
                        type: this.getType(this.type),
                        animationPlayed: true,
                        lineThickness: 3
                    };
                })
                : [
                    {
                        fillColorsField: "color",
                        fillAlphas: this.type == ChartTypes.AreaChart ? 0.4 : lineCharts.indexOf(this.type) != -1 ? 0 : 1,
                        lineAlpha: barCharts.indexOf(this.type) != -1 ? 0 : 1,
                        balloonText: this.formatTooltip,
                        valueField: "data",
                        labelText: "[[data]]",
                        type: this.getType(this.type),
                        animationPlayed: true,
                        lineThickness: 3
                    }
                ];
        } else if (baseType == ChartBaseTypes.Pie) {
            //Pie
            options.innerRadius = this.type == ChartTypes.Donut ? "50%" : "0%";
            options.autoMargins = false;
            options.pullOutRadius = 20;
            options.labelText = this.showHoveringLabels ? `${this.formatTooltip} ([[percents]]%)` : "";
            options.balloonText = `${this.formatTooltip} ([[percents]]%)`;
            options.titleField = "label";
            options.colorField = "color";
            options.valueField = "data";
        } else if (baseType == ChartBaseTypes.Funnel) {
            //Funnel
            options.colorField = "color";
            options.valueField = "data";
            options.titleField = "label";
            options.depth3D = 1;
            options.marginRight = 240;

            options.angle = 40;
            options.outlineAlpha = 1;
            options.outlineColor = "#FFFFFF";
            options.outlineThickness = 2;
            options.labelPosition = "right";
            options.balloonText = "[[label]]: [[data]]";
        }


        if (baseType == ChartBaseTypes.Pie
            || baseType == ChartBaseTypes.Funnel
            || baseType == ChartBaseTypes.Serial) {

            if (this.orderBy && this.orderBy.trim() != "") {
                this.dataEntries.chartDataEntries = (this.orderBy == "descending" ?
                    Enumerable.from(this.dataEntries.chartDataEntries).orderByDescending(x => !isNaN(parseFloat(x.data.toString())) && isFinite(Number(x.data)) ? Number(x.data) : x.data) :
                    Enumerable.from(this.dataEntries.chartDataEntries).orderBy(x => !isNaN(parseFloat(x.data.toString())) && isFinite(Number(x.data)) ? Number(x.data) : x.data)
                ).toArray();
            }

            options.dataProvider = this.getDataProvider();

        } else if (baseType == ChartBaseTypes.Gauge) {
            const bands = [];
            const labels = [];
            const start = 25;
            const each = (100 - start) / this.dataEntries.chartDataEntries.length;
            const max = Enumerable.from(this.dataEntries.chartDataEntries).sum(x => x.data ? Number(x.data) : 0);
            for (const item of this.dataEntries.chartDataEntries) {
                const index = this.dataEntries.chartDataEntries.indexOf(item);
                const posStart = start + (index * each);
                const posEnd = posStart + each;
                const value = (Number(item.data) / max) * 100;
                bands.push({
                    color: "#eee",
                    startValue: 0,
                    endValue: 100,
                    radius: `${posEnd - 2}%`,
                    innerRadius: `${posStart + 2}%`
                });

                bands.push({
                    color: item.color,
                    startValue: 0,
                    endValue: value,
                    radius: `${posEnd - 2}%`,
                    innerRadius: `${posStart + 2}%`,
                    balloonText: `${value.toFixed(2)}%`
                });

                const lineEach = 31.5 / this.dataEntries.chartDataEntries.length;
                const percent = ((this.dataEntries.chartDataEntries.length - index) * lineEach);
                labels.push({
                    text: item.label,
                    x: "48%",
                    y: `${percent}%`,
                    size: this.fontSize,
                    bold: true,
                    color: item.color,
                    align: "right"
                });
            }

            options.axes = [{
                axisAlpha: 0,
                tickAlpha: 0,
                labelsEnabled: false,
                startValue: 0,
                endValue: 100,
                startAngle: 0,
                endAngle: 270,
                bands: bands
            }];
            options.allLabels = labels;
            options.graphs = [{ "adaptiveLabelPosition": true }]
        } else if (baseType == ChartBaseTypes.XY) {
            options.graphs = [{
                "balloonText": "<div style='margin:5px;'>[[label]]</div>",
                "bullet": "round",
                "maxBulletSize": 25,
                "lineColor": "#418CF1",
                "xField": "label",
                "yField": "data",
                "labelText": "[[formatData]]"
            }];

            options.dataProvider = this.dataEntries.chartDataEntries;

            options.valueAxes = this.axes;
        }
    }

    private getGuidFromLabel(label: string) {
        if (this._guids[label]) {
            return this._guids[label];
        }
        this._guids[label] = Guid.generateGuid();
        return this._guids[label];
    }

    private getDataProvider(): any {
        return this.dataEntries.labels ?
            this.dataEntries.labels.map(x => {
                let date = this.dateFormat ? moment(x).format(this.dateFormat) : "Invalid date";
                if (date == "Invalid date") {
                    date = x;
                }
                const data: any = {
                    label: date
                };
                for (const item of this.dataEntries.chartDataEntries) {
                    const label = this.getGuidFromLabel(item.label);
                    //get rounded number if data is number
                    data[`${label}-data`] = this.getValueData(item.data[this.dataEntries.labels.indexOf(x)]);
                    data[`${label}-color`] = item.color;
                    data[`${label}-formatData`] = (item.formatData ? `${this.getLocaleFormatValue(item.formatData[this.dataEntries.labels.indexOf(x)])} ${x}` : (this.getLocaleFormatValue(data[`${label}-data`]) + ' ' + x));
                    data[`${label}-formatDataStacked`] = (this.getLocaleFormatValue(data[`${label}-data`]) + ' ' + x);
                }
                return data;
            })
            : Enumerable.from(this.dataEntries.chartDataEntries).where(x => x.label == "All").concat(Enumerable.from(this.dataEntries.chartDataEntries).where(x => x.label != "All")).toArray();
    }

    //added to rounded number if data is number
    getValueData(data) {
        if (!isNaN(parseFloat(data)) && isFinite(data)) {
            return (data % 1 === 0 ? Number(data) : Number(data));
        }
        else {
            return data;
        }
    }

    getLocaleFormatValue(data) {
        if (!isNaN(parseFloat(data)) && isFinite(data)) {
            return data.toString();
        }
        else return data;
    }

    getType(type: string) {
        switch (type) {
            case ChartTypes.Line:
                return "line";
            case ChartTypes.Bar:
            case ChartTypes.HorizontalBar:
            case ChartTypes.StackedHorizontalBar:
            case ChartTypes.StackedBar:
                return "column";
            case ChartTypes.Donut:
                return "donut";
            case ChartTypes.Pie:
                return "pie";
            case ChartTypes.SolidGauge:
                return "gauge";
            case ChartTypes.AreaChart:
                return "line";
            case ChartTypes.XYChart:
                return "xy";
        }

        return "";
    }

    private shuffle(array: string[]): string[] {
        let currentIndex = array.length, temporaryValue, randomIndex;

        // while there remain elements to shuffle...
        while (0 !== currentIndex) {

            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            // and swap it with the current element.
            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }

        return array;
    }
}

export class ChartTypes {
    public static Funnel: string = "Funnel Chart";
    public static Line: string = "Line Chart";
    public static StackedBar: string = "Stacked Bar Chart";
    public static StackedHorizontalBar: string = "Stacked Horizontal Bar Chart";
    public static Bar: string = "Bar Chart";
    public static HorizontalBar: string = "Horizontal Bar Chart";
    public static Donut: string = "Donut Chart";
    public static Pie: string = "Pie Chart";
    public static SolidGauge: string = "Solid Gauge Chart";
    public static AreaChart: string = "Area Chart";
    public static XYChart: string = "XY Chart";
}

export class ChartBaseTypes {
    public static Funnel: string = "funnel";
    public static Serial: string = "serial";
    public static Pie: string = "pie";
    public static Gauge: string = "gauge";
    public static XY: string = "xy";
}
